import * as sessionReplay from "@amplitude/session-replay-browser";
import { IdentifyTraits, RudderAnalytics } from "@rudderstack/analytics-js";
import { AffiliateDestination } from "../types/landingPage";
import { getAbTestProperties } from "./abTest";
import {
  getCookie,
  getHadAuthBefore,
  HM_TOKEN,
  removeHadAuthBefore,
  setHadAuthBefore,
} from "./authToken";
import { identifyUser } from "./sentry";

const RUDDER_ANONYMOUS_ID_COOKIE = "rl_anonymous_id";
const RUDDER_ENCRYPTION_PREFIX_V3 = "RS_ENC_v3_";

/**
 * Reads the Rudderstack anonymous ID directly from its cookie so it can be
 * inspected before the SDK has finished loading. The SDK stores the ID as a JSON
 * string, base64-encoded behind the RS_ENC_v3_ prefix when storage encryption
 * v3 is enabled (our configuration).
 */
function readRudderAnonymousIdCookie(): string | null {
  const raw = getCookie(RUDDER_ANONYMOUS_ID_COOKIE);
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    const json = decoded.startsWith(RUDDER_ENCRYPTION_PREFIX_V3)
      ? atob(decoded.slice(RUDDER_ENCRYPTION_PREFIX_V3.length))
      : decoded;
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === "string" && parsed ? parsed : null;
  } catch {
    return null;
  }
}

const RUDDERSTACK_WRITE_KEY = import.meta.env.VITE_RUDDERSTACK_API_KEY;
const RUDDERSTACK_DATA_PLANE_URL = import.meta.env
  .VITE_RUDDERSTACK_DATA_PLANE_URL;
const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;
const KETCH_ENABLED = !!import.meta.env.VITE_KETCH_TAG_SRC;

type PendingAnalyticsCall = () => void;

let rudderAnalytics: RudderAnalytics | undefined;
let isRudderstackReady = false;
let isSessionReplayEnabled = false;
let pendingAnalyticsCalls: PendingAnalyticsCall[] = [];

const runWhenRudderstackReady = (call: PendingAnalyticsCall) => {
  if (isRudderstackReady) {
    call();
    return;
  }

  pendingAnalyticsCalls.push(call);
};

const flushPendingAnalyticsCalls = () => {
  isRudderstackReady = true;
  const calls = pendingAnalyticsCalls;
  pendingAnalyticsCalls = [];
  calls.forEach((call) => call());
};

const sessionReplayProperties = (): Record<string, unknown> =>
  isSessionReplayEnabled ? sessionReplay.getSessionReplayProperties() : {};

export const initRudderstack = () => {
  // Check if we should initialize Rudderstack
  const shouldInitialize =
    import.meta.env.PROD || import.meta.env.VITE_ENABLE_RUDDERSTACK === "true";

  if (!shouldInitialize) {
    console.log("Rudderstack not initialized: Development environment");
    flushPendingAnalyticsCalls();
    return;
  }

  const analytics = new RudderAnalytics();
  rudderAnalytics = analytics;

  const authToken = getCookie(HM_TOKEN);
  const hadAuthBefore = getHadAuthBefore();
  if (authToken) {
    // User is authenticated - ensure tracking cookie is set
    setHadAuthBefore();
  } else if (hadAuthBefore) {
    // User WAS authenticated but token is gone - reset analytics anonymous ID
    const anonymousIdBefore = readRudderAnonymousIdCookie();
    analytics.reset(true);
    // Calls made before load() sit in the SDK's buffer until its remote config
    // has loaded and are lost if the page unloads first, so only clear the
    // marker once the ID is confirmed to have rotated; otherwise the next page
    // load tries again.
    analytics.ready(() => {
      const anonymousIdAfter = analytics.getAnonymousId();
      if (
        anonymousIdBefore &&
        anonymousIdAfter &&
        anonymousIdAfter !== anonymousIdBefore
      ) {
        removeHadAuthBefore();
      }
    });
  }
  analytics.load(RUDDERSTACK_WRITE_KEY, RUDDERSTACK_DATA_PLANE_URL, {
    storage: {
      migrate: true,
      encryption: {
        version: "v3",
      },
    },
    ...(KETCH_ENABLED
      ? {
          consentManagement: { enabled: true, provider: "ketch" },
          // Until Ketch reports the visitor's choice only the anonymous id is persisted and
          // events still go out immediately, so attribution page views are never delayed or
          // lost. Destinations that need analytics consent drop them server-side.
          preConsent: {
            enabled: true,
            storage: { strategy: "anonymousId" },
            events: { delivery: "immediate" },
          },
        }
      : {}),
    integrations: {
      All: true,
      ...Object.values(AffiliateDestination).reduce(
        (acc: Record<string, boolean>, destination: AffiliateDestination) => {
          acc[destination] = false;
          return acc;
        },
        {},
      ),
    },
  });

  console.log("Rudderstack initialized");

  // Initialize Session Replay after Rudderstack is ready
  if (import.meta.env.VITE_ENABLE_AMPLITUDE_SESSION_REPLAY === "true") {
    console.log("Initializing Amplitude session replay");

    analytics.ready(function () {
      const rudderAnonymousId = analytics.getAnonymousId();
      const rudderSessionId = analytics.getSessionId() ?? undefined;

      sessionReplay.init(AMPLITUDE_API_KEY, {
        deviceId: rudderAnonymousId,
        sessionId: rudderSessionId,
        sampleRate: 0.1, // 10% of sessions will be captured
      }).promise;

      // track() and page() below merge the session replay properties into
      // every event once this flag is set
      isSessionReplayEnabled = true;

      // Update session ID when it changes
      if (rudderSessionId !== undefined) {
        sessionReplay.setSessionId(rudderSessionId);
      }
    });
  }

  analytics.ready(flushPendingAnalyticsCalls);
};

/**
 * Publishes the Ketch purposes (purpose code -> granted) so the SDK stamps
 * context.consentManagement on every subsequent event.
 */
export const updateConsent = (purposes: Record<string, boolean>) => {
  if (!KETCH_ENABLED) return;
  runWhenRudderstackReady(() => {
    if (rudderAnalytics) {
      // The SDK's Ketch plugin reads window.ketchConsent in preference to the Ketch cookie
      window.ketchConsent = purposes;
      rudderAnalytics.consent();
    }
  });
};

export const identify = (userId?: string, traits?: Record<string, unknown>) => {
  runWhenRudderstackReady(() => {
    if (rudderAnalytics) {
      const identifyTraits = (traits ?? null) as IdentifyTraits | null;
      if (userId) {
        rudderAnalytics.identify(userId, identifyTraits);
      } else {
        rudderAnalytics.identify(identifyTraits);
      }
      identifyUser(userId, traits);
    }
  });
};

const createIntegrationsObject = (affiliateDestinations?: string[]) => {
  return Object.values(AffiliateDestination).reduce(
    (acc: Record<string, boolean>, destination: AffiliateDestination) => {
      if (!affiliateDestinations?.includes(destination)) {
        acc[destination] = false;
      }
      return acc;
    },
    {},
  );
};

const createAnalyticsOptions = (affiliateDestinations?: string[]) => {
  const integrations = createIntegrationsObject(affiliateDestinations);
  return { integrations };
};

export const track = (
  event: string,
  properties?: Record<string, unknown>,
  affiliateDestinations?: string[],
) => {
  runWhenRudderstackReady(() => {
    if (rudderAnalytics) {
      const { integrations } = createAnalyticsOptions(affiliateDestinations);
      const abTestProps = getAbTestProperties();
      const enrichedProperties = {
        ...properties,
        ...abTestProps,
        platform: "LP",
        ...sessionReplayProperties(),
      };
      rudderAnalytics.track(event, enrichedProperties, {
        integrations,
      });
    }
  });
};

export const getAnonymousId = (): string | undefined => {
  if (isRudderstackReady && rudderAnalytics) {
    return rudderAnalytics.getAnonymousId();
  }
  return undefined;
};

export const page = (
  category?: string,
  name?: string,
  properties?: Record<string, unknown>,
  affiliateDestinations?: string[],
) => {
  runWhenRudderstackReady(() => {
    if (rudderAnalytics) {
      const { integrations } = createAnalyticsOptions(affiliateDestinations);
      const abTestProps = getAbTestProperties();
      const enrichedProperties = {
        ...properties,
        ...abTestProps,
        platform: "LP",
        page_url_full: location.href,
        ...sessionReplayProperties(),
      };
      const options = { integrations };
      if (category !== undefined) {
        rudderAnalytics.page(category, name ?? "", enrichedProperties, options);
      } else if (name !== undefined) {
        rudderAnalytics.page(name, enrichedProperties, options);
      } else {
        rudderAnalytics.page(enrichedProperties, options);
      }
    }
  });
};
